import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogManager = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    excerpt: { fr: '', en: '' },
    content: { fr: '', en: '' },
    category: { fr: '', en: '' },
    image: '',
    published: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEdit = () => {
    setEditingPost(null);
    setFormData({
      title: { fr: '', en: '' },
      excerpt: { fr: '', en: '' },
      content: { fr: '', en: '' },
      category: { fr: '', en: '' },
      image: '',
      published: false
    });
    setDialogOpen(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image: post.image,
      published: post.published
    });
    setDialogOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      await axios.delete(`${API}/blog/post/${postId}`, getAuthHeaders());
      toast.success('Article supprimé avec succès');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPost) {
        await axios.put(
          `${API}/blog/post/${editingPost.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Article mis à jour avec succès');
      } else {
        await axios.post(
          `${API}/blog/post`,
          formData,
          getAuthHeaders()
        );
        toast.success('Article créé avec succès');
      }

      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(
        `${API}/blog/post/${post.id}`,
        { published: !post.published },
        getAuthHeaders()
      );
      toast.success(post.published ? 'Article dépublié' : 'Article publié');
      fetchPosts();
    } catch (error) {
      console.error('Error toggling published:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Gestion du Blog</h1>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-24 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion du Blog</h1>
          <p className="text-slate-600 mt-2">{posts.length} articles au total</p>
        </div>
        <Button
          onClick={handleCreateEdit}
          className="bg-blue-900 hover:bg-blue-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvel article
        </Button>
      </div>

      <div className="space-y-4">
        {posts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">Aucun article pour le moment</p>
              <Button
                onClick={handleCreateEdit}
                className="mt-4 bg-blue-900 hover:bg-blue-800"
              >
                Créer le premier article
              </Button>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card key={post.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  {post.image && (
                    <img
                      src={post.image}
                      alt={post.title.fr}
                      className="w-full lg:w-32 h-32 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{post.title.fr}</h3>
                        <p className="text-sm text-slate-600 mt-1">{post.excerpt.fr}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={post.category.fr === 'Conseils' ? 'bg-blue-100 text-blue-800' : ''}>
                          {post.category.fr}
                        </Badge>
                        <Badge
                          className={
                            post.published
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : 'bg-slate-100 text-slate-800 hover:bg-slate-100'
                          }
                        >
                          {post.published ? 'Publié' : 'Brouillon'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button
                        onClick={() => togglePublished(post)}
                        variant="outline"
                        size="sm"
                      >
                        {post.published ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-2" />
                            Dépublier
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            Publier
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleEdit(post)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifier
                      </Button>
                      <Button
                        onClick={() => handleDelete(post.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* French Fields */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="font-semibold text-slate-900">Version Française</h3>
              <div>
                <Label htmlFor="title-fr">Titre (FR)</Label>
                <Input
                  id="title-fr"
                  value={formData.title.fr}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: { ...formData.title, fr: e.target.value }
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="excerpt-fr">Extrait (FR)</Label>
                <Textarea
                  id="excerpt-fr"
                  value={formData.excerpt.fr}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      excerpt: { ...formData.excerpt, fr: e.target.value }
                    })
                  }
                  rows={2}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content-fr">Contenu (FR)</Label>
                <Textarea
                  id="content-fr"
                  value={formData.content.fr}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      content: { ...formData.content, fr: e.target.value }
                    })
                  }
                  rows={5}
                  required
                />
              </div>
              <div>
                <Label htmlFor="category-fr">Catégorie (FR)</Label>
                <Input
                  id="category-fr"
                  value={formData.category.fr}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: { ...formData.category, fr: e.target.value }
                    })
                  }
                  required
                />
              </div>
            </div>

            {/* English Fields */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="font-semibold text-slate-900">English Version</h3>
              <div>
                <Label htmlFor="title-en">Title (EN)</Label>
                <Input
                  id="title-en"
                  value={formData.title.en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: { ...formData.title, en: e.target.value }
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="excerpt-en">Excerpt (EN)</Label>
                <Textarea
                  id="excerpt-en"
                  value={formData.excerpt.en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      excerpt: { ...formData.excerpt, en: e.target.value }
                    })
                  }
                  rows={2}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content-en">Content (EN)</Label>
                <Textarea
                  id="content-en"
                  value={formData.content.en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      content: { ...formData.content, en: e.target.value }
                    })
                  }
                  rows={5}
                  required
                />
              </div>
              <div>
                <Label htmlFor="category-en">Category (EN)</Label>
                <Input
                  id="category-en"
                  value={formData.category.en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: { ...formData.category, en: e.target.value }
                    })
                  }
                  required
                />
              </div>
            </div>

            {/* Other Fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="image">URL de l'image</Label>
                <Input
                  id="image"
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="published"
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) =>
                    setFormData({ ...formData, published: e.target.checked })
                  }
                  className="rounded border-slate-300"
                />
                <Label htmlFor="published" className="cursor-pointer">
                  Publier immédiatement
                </Label>
              </div>
            </div>

            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                {editingPost ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
